'use client'

import { useState, useEffect } from 'react'
import {
  Col,
  Row,
  Typography,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalPortal,
  Badge,
} from '@repo/ui/components'
import { useTranslate } from '@repo/shared/hooks'
import QRCode from 'qrcode'
import Image from 'next/image'

interface CryptoQrCodeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  value: string
  networkName?: string
  networkBadge?: string
  networkIcon?: React.ReactNode
}

export const CryptoQrCodeModal = ({
  open,
  onOpenChange,
  value,
  networkName,
  networkBadge,
  networkIcon,
}: CryptoQrCodeModalProps) => {
  const { t } = useTranslate()
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('')
  const [qrCodeError, setQrCodeError] = useState<string>('')

  useEffect(() => {
    if (open && value) {
      const generateQRCode = async () => {
        try {
          const qrDataUrl = await QRCode.toDataURL(value, {
            width: 300,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF',
            },
          })
          setQrCodeDataUrl(qrDataUrl)
          setQrCodeError('')
        } catch (error) {
          console.error('Error generating QR code:', error)
          setQrCodeError('Failed to generate QR code')
        }
      }
      generateQRCode()
    }
  }, [open, value])

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalPortal>
        <ModalOverlay />
        <ModalContent className='sm:max-w-[400px]'>
          {networkName && (
            <ModalHeader>
              <Row className='gap-xs items-center justify-center '>
                {networkIcon && <div className='mt-2'>{networkIcon}</div>}

                <Typography size='sm' color='text-neutral-surface-60'>
                  {t('common_network')}
                </Typography>
                {networkBadge && <Badge color='alert'>{networkBadge}</Badge>}
              </Row>
            </ModalHeader>
          )}
          <Col className='gap-md items-center px-md'>
            {qrCodeDataUrl ? (
              <div className='bg-white p-md rounded-md'>
                <Image
                  src={qrCodeDataUrl}
                  alt='QR Code'
                  width={240}
                  height={240}
                />
              </div>
            ) : qrCodeError ? (
              <Typography size='sm' color='text-neutral-surface-60'>
                {qrCodeError}
              </Typography>
            ) : (
              <Typography size='sm' color='text-neutral-surface-60'>
                {t('common_loading')}
              </Typography>
            )}
            <Typography
              size='sm'
              color='text-neutral-surface-100'
              className='font-mono text-center break-all'
            >
              {value}
            </Typography>
            <Button
              variant='ghost'
              color='neutral'
              size='md'
              width='full'
              onClick={() => onOpenChange(false)}
            >
              {t('common_got_it')}
            </Button>
          </Col>
        </ModalContent>
      </ModalPortal>
    </Modal>
  )
}
